let navigation = {
  load() {
    document.addEventListener("DOMContentLoaded", () => {
      const burger = document.querySelector(".SiteHeader-burger");
      const menuTop = document.querySelector(".MenuTop");
      const menuItems = document.querySelectorAll(
        ".menu-item-has-children > a",
      );
      const header = document.querySelector(".SiteHeader");

      if (!burger || !menuTop || !header) return;

      let lastScrollTop = 0;
      let openByKeyboard = false;

      /* ==========================================
         CONFIG
         true  = menu burger sur desktop + mobile
         false = menu burger uniquement mobile
      ========================================== */
      const BURGER_ALL_DEVICES = false;

      const isMobile = () =>
        window.matchMedia("(max-width: 1023.98px)").matches;

      const isBurgerMode = () => {
        if (BURGER_ALL_DEVICES) return true;
        return isMobile();
      };

      /* ==========================================
         Classes de mode
      ========================================== */
      const updateHeaderModeClass = () => {
        if (BURGER_ALL_DEVICES) {
          header.classList.add("-burgerEverywhere");
        } else {
          header.classList.remove("-burgerEverywhere");
        }
      };

      /* ==========================================
         Scroll lock (mobile menu / burger mode)
      ========================================== */
      const lockScroll = () => {
        document.documentElement.classList.add("is-menu-open");
        document.body.classList.add("is-menu-open");
      };

      const unlockScroll = () => {
        document.documentElement.classList.remove("is-menu-open");
        document.body.classList.remove("is-menu-open");
      };

      /* ==========================================
         Focus helpers
      ========================================== */
      const safeFocus = (el) => {
        if (!el) return;
        try {
          el.focus({ preventScroll: true });
        } catch (err) {
          el.focus();
        }
      };

      const setMenuFocusable = (focusable) => {
        if (!isBurgerMode()) {
          menuTop.setAttribute("aria-hidden", "false");
          menuTop
            .querySelectorAll("a")
            .forEach((a) => a.removeAttribute("tabindex"));
          return;
        }

        menuTop.setAttribute("aria-hidden", focusable ? "false" : "true");
        menuTop.querySelectorAll("a").forEach((a) => {
          if (focusable) a.removeAttribute("tabindex");
          else a.setAttribute("tabindex", "-1");
        });
      };

      const focusFirstMenuLink = () => {
        const first = menuTop.querySelector("a");
        if (first) safeFocus(first);
      };

      let removeTrapHandlers = null;

      const getVisibleMenuLinks = () => {
        return Array.from(menuTop.querySelectorAll("a[href]")).filter(
          (el) =>
            el.getClientRects().length > 0 &&
            el.getAttribute("tabindex") !== "-1",
        );
      };

      const getFirstMenuLink = () => {
        const links = getVisibleMenuLinks();
        return links[0] || null;
      };

      const getLastMenuLink = () => {
        const links = getVisibleMenuLinks();
        return links[links.length - 1] || null;
      };

      const activateFocusTrap = () => {
        if (removeTrapHandlers) return;

        const onKeyDown = (e) => {
          if (!isBurgerMode()) return;
          if (!document.body.classList.contains("is-menu-open")) return;
          if (e.key !== "Tab") return;

          const firstLink = getFirstMenuLink();
          const lastLink = getLastMenuLink();

          if (!firstLink || !lastLink) return;

          const active = document.activeElement;

          if (active !== burger && !menuTop.contains(active)) {
            e.preventDefault();
            safeFocus(firstLink);
            return;
          }

          if (e.shiftKey && active === firstLink) {
            e.preventDefault();
            safeFocus(burger);
            return;
          }

          if (e.shiftKey && active === burger) {
            e.preventDefault();
            safeFocus(lastLink);
            return;
          }

          if (!e.shiftKey && active === lastLink) {
            e.preventDefault();
            safeFocus(burger);
            return;
          }

          if (!e.shiftKey && active === burger) {
            e.preventDefault();
            safeFocus(firstLink);
          }
        };

        const onFocusIn = (e) => {
          if (!isBurgerMode()) return;
          if (!document.body.classList.contains("is-menu-open")) return;

          const firstLink = getFirstMenuLink();
          if (!firstLink) return;

          const insideMenu = menuTop.contains(e.target);
          const onBurger = e.target === burger;

          if (!insideMenu && !onBurger) {
            safeFocus(firstLink);
          }
        };

        document.addEventListener("keydown", onKeyDown);
        document.addEventListener("focusin", onFocusIn);

        removeTrapHandlers = () => {
          document.removeEventListener("keydown", onKeyDown);
          document.removeEventListener("focusin", onFocusIn);
          removeTrapHandlers = null;
        };
      };

      const deactivateFocusTrap = () => {
        if (removeTrapHandlers) removeTrapHandlers();
      };

      /* ==========================================
         Sous-menus helpers
      ========================================== */
      const parentItems = document.querySelectorAll(".menu-item-has-children");

      const closeAllSubmenus = () => {
        parentItems.forEach((li) => {
          const submenu = li.querySelector(
            ":scope > .submenu, :scope > .sub-menu",
          );
          const link = li.querySelector(":scope > a");

          if (submenu) {
            submenu.classList.remove("active");
            submenu.style.display = "none";
          }
          if (link) link.setAttribute("aria-expanded", "false");
        });
      };

      /* ==========================================
         Menu open/close
      ========================================== */
      const openMenu = () => {
        if (!isBurgerMode()) return;

        burger.classList.add("animate");
        menuTop.classList.add("animate");
        lockScroll();

        burger.setAttribute("aria-expanded", "true");

        setMenuFocusable(true);
        activateFocusTrap();

        if (openByKeyboard) {
          focusFirstMenuLink();
        } else {
          safeFocus(burger);
        }
      };

      const closeMenu = () => {
        burger.classList.remove("animate");
        menuTop.classList.remove("animate");
        burger.setAttribute("aria-expanded", "false");

        if (document.body.classList.contains("is-menu-open")) {
          unlockScroll();
        }

        deactivateFocusTrap();
        setMenuFocusable(false);
        closeAllSubmenus();

        if (isBurgerMode()) safeFocus(burger);
      };

      const toggleMenu = () => {
        if (!isBurgerMode()) return;
        burger.classList.contains("animate") ? closeMenu() : openMenu();
      };

      burger.addEventListener("click", (e) => {
        e.preventDefault();
        e.stopPropagation();
        openByKeyboard = false;
        toggleMenu();
      });

      burger.addEventListener("keydown", (e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          openByKeyboard = true;
          toggleMenu();
        }
      });

      /* ==========================================
         Prevent parent navigation in burger mode
      ========================================== */
      menuItems.forEach((item) => {
        item.addEventListener("click", (e) => {
          if (isBurgerMode()) e.preventDefault();
        });
      });

      /* ==========================================
         Click outside
      ========================================== */
      document.addEventListener("click", (e) => {
        if (!document.body.classList.contains("is-menu-open")) return;

        if (!menuTop.contains(e.target) && !burger.contains(e.target)) {
          closeMenu();
        }
      });

      /* ==========================================
         ESC
      ========================================== */
      document.addEventListener("keydown", (e) => {
        if (e.key !== "Escape") return;

        if (document.body.classList.contains("is-menu-open")) {
          closeMenu();
        } else {
          closeAllSubmenus();
        }
      });

      /* ==========================================
         Header hide on scroll
      ========================================== */
      window.addEventListener("scroll", () => {
        const scrollTop =
          window.pageYOffset || document.documentElement.scrollTop;
        const scrollingUp = scrollTop < lastScrollTop;

        // En burger le header reste toujours visible : pas de masquage, et donc
        // pas de is-scrolling-up (le fond uni est géré en CSS).
        if (isBurgerMode() || document.body.classList.contains("is-menu-open")) {
          header.style.top = "0";
          header.classList.remove("is-scrolling-up");
          lastScrollTop = scrollTop;
          return;
        }

        header.style.top = scrollingUp ? "0" : "-280px";
        header.classList.toggle(
          "is-scrolling-up",
          scrollingUp && scrollTop > 0,
        );
        lastScrollTop = scrollTop;
      });

      /* ==========================================
         Resize handling
      ========================================== */
      function updateClass() {
        updateHeaderModeClass();

        if (isBurgerMode()) {
          menuTop.classList.add("-mobileVersion");
          const isOpen = burger.classList.contains("animate");
          setMenuFocusable(isOpen);
          header.style.top = "0";
          header.classList.remove("is-scrolling-up");
        } else {
          menuTop.classList.remove("-mobileVersion");
          if (document.body.classList.contains("is-menu-open")) {
            closeMenu();
          }
          setMenuFocusable(true);
          closeAllSubmenus();
        }
      }

      updateClass();
      window.addEventListener("resize", updateClass);

      /* ==========================================
         Sous-menus desktop + burger
      ========================================== */
      parentItems.forEach((li) => {
        const link = li.querySelector(":scope > a");
        const submenu = li.querySelector(
          ":scope > .submenu, :scope > .sub-menu",
        );
        if (!link || !submenu) return;

        submenu.style.display = "none";
        link.setAttribute("aria-expanded", "false");
        link.setAttribute("aria-haspopup", "true");

        link.addEventListener("click", (e) => {
          e.preventDefault();
          e.stopPropagation();

          const willOpen = !submenu.classList.contains("active");

          setTimeout(() => {
            closeAllSubmenus();

            if (willOpen) {
              submenu.classList.add("active");
              submenu.style.display = "block";
              link.setAttribute("aria-expanded", "true");
            }
          }, 0);
        });

        const submenuLinks = submenu.querySelectorAll("a");
        if (!submenuLinks.length) return;

        const firstLink = submenuLinks[0];
        const lastLink = submenuLinks[submenuLinks.length - 1];

        lastLink.addEventListener("keydown", (e) => {
          if (isBurgerMode()) return;
          if (e.key === "Tab" && !e.shiftKey) {
            submenu.classList.remove("active");
            submenu.style.display = "none";
            link.setAttribute("aria-expanded", "false");
          }
        });

        firstLink.addEventListener("keydown", (e) => {
          if (isBurgerMode()) return;
          if (e.key === "Tab" && e.shiftKey) {
            submenu.classList.remove("active");
            submenu.style.display = "none";
            link.setAttribute("aria-expanded", "false");
          }
        });
      });

      document.addEventListener("click", (e) => {
        if (!e.target.closest(".menu-item-has-children")) {
          closeAllSubmenus();
        }
      });
    });
  },
};

export default navigation;
